import { useState, useContext, useEffect } from "react";
import TollCard from "./TollCard";
import TollInfo from "./TollInfo";
import Heading from "../UI/Heading";
import { UtilityContext } from "../../context/util-context";
import { useLocation } from "react-router-dom";
import { requestAdminAPI } from "../../api/posh-api";
import DateFilter from "../UI/DateFilter";
import PaginationMenu from "../UI/PaginationMenu";
import NoRecordsFound from "../UI/NoRecordsFound";
import { endOfMonth, startOfMonth } from "date-fns";
import LoadingCard from "../UI/LoadingCard";
import ExportCsvButton from "../UI/ExportCsvButton";
import { MdRefresh } from "react-icons/md";
import FilterMenu from "../UI/FilterMenu";
import SearchBar from "../UI/SearchBar";

/** ========================================================================
 * * Tolls Page
 * Renders a page, which displays all Tolls records.
 * ========================================================================== */

export default function Tolls() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filters
  const defaultFilters = {
    query: "",
    sd: startOfMonth(new Date()).toISOString(),
    ed: endOfMonth(new Date()).toISOString(),
    invoiced: "all",
    limit: 100,
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  // Initialize States
  const [tolls, setTolls] = useState([]);
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(false);
  const [selectedToll, setSelectedToll] = useState(null);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);
      const data = await requestAdminAPI("search", {
        table: "toll",
        filters: filter,
        limit: filter.limit,
      });

      // Replace URL
      let url = "/tolls?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setTolls(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== FILTER OPTIONS ===================
  const filterOptions = [
    {
      label: "Invoiced",
      options: [
        { label: "All", value: "all" },
        { label: "Yes", value: "y" },
        { label: "No", value: "n" },
      ],
      selected: filter.invoiced,
    },
  ];

  // Calculate Total
  let total = 0;
  tolls.forEach((toll) => {
    total += Number(toll.amount);
  });

  // Apply Filters
  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="container">
      <Heading
        title="Toll Records"
        text="Tolls are fetched from NTTA every morning at 9:30 am and invoiced to customers immediately. Green border indicates the toll is invoiced. Red and yellow borders indicate not invoiced."
      >
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
          defaultPeriod="Month"
        />
        <FilterMenu
          onSubmit={(f) => setFilter({ ...filter, ...f })}
          options={filterOptions}
        />
        <ExportCsvButton array={tolls} filename="tolls" />
        <MdRefresh className="icon" onClick={fetchRecords} />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      {loading ? (
        <LoadingCard columns={4} max={5} />
      ) : tolls.length > 0 ? (
        <div className="list">
          {tolls.map((toll, index) => (
            <TollCard
              toll={toll}
              key={index}
              onClick={(t) => setSelectedToll(t)}
            />
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        limit={filter.limit}
        setLimit={(limit) => setFilter({ ...filter, limit })}
        count={tolls.length}
        loading={loading}
      >
        <p className="text-stone-400">
          Total:{" "}
          <span className="font-semibold text-stone-200">
            ${total.toFixed(2)}
          </span>
        </p>
      </PaginationMenu>
      <TollInfo
        open={selectedToll ? true : false}
        setOpen={() => setSelectedToll(null)}
        tollId={selectedToll?.id}
      />
    </div>
  );
}
